import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Calendar, ArrowRight, Edit, Trash, Share2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { TaskCard as TaskCardType, MFOItem } from "@/types/kanban";
import MFODetailDialog from "../mfo/MFODetailDialog";
import TransferMFOModal from "./TransferMFOModal";
import DeleteMFOConfirmModal from "./DeleteMFOConfirmModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";

interface TaskCardProps {
  card: TaskCardType;
  columnId: string;
  onDragStart: (task: any) => void;
  onDelete?: (taskId: string) => void;
  onTransfer?: (cardId: string, newColumnId: string) => void;
  isDisabled?: boolean;
}

export const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "high":
      return "bg-red-100 text-red-800 hover:bg-red-200";
    case "medium":
      return "bg-amber-100 text-amber-800 hover:bg-amber-200";
    case "low":
      return "bg-blue-100 text-blue-800 hover:bg-blue-200";
    default:
      return "bg-gray-100 text-gray-800 hover:bg-gray-200";
  }
};

export const getProgressColor = (progress: number) => {
  if (progress < 30) return "bg-red-500";
  if (progress < 70) return "bg-amber-500";
  return "bg-green-500";
};

const TaskCard = ({ card, columnId, onDragStart, onDelete, onTransfer, isDisabled }: TaskCardProps) => {
  const [isMFODialogOpen, setIsMFODialogOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const navigate = useNavigate();

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const mfoItem: MFOItem = {
    id: card.id,
    code: card.id.substring(0, 7).toUpperCase(),
    title: card.title,
    successIndicators: [card.description],
    budget: 1000000,
    timeline: { q1: true, q2: true, q3: false, q4: false },
    collaborators: card.assignees,
    departmentId: card.departmentId || "dept-tourism",
    status: columnId as any,
    priority: card.priority,
    progress: card.progress,
    dueDate: card.dueDate
  };

  const handleViewMFO = () => {
    navigate(`/dashboard/mfo/${card.id}`);
  };

  const handleEditMFO = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditModalOpen(true);
  };

  const handleTransferMFO = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsTransferModalOpen(true);
  };

  const handleDeleteMFO = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDeleteModalOpen(true);
  };
  
  const handleConfirmDelete = () => {
    if (onDelete) {
      onDelete(card.id);
    }
    setIsDeleteModalOpen(false);
    toast({
      title: "MFO Deleted",
      description: `"${card.title}" has been deleted`,
      variant: "destructive",
    });
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isDisabled && onDragStart) {
      setIsDragging(true);
      onDragStart(card);
      e.stopPropagation();
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <>
      <Card 
        className={`animate-scale shadow-sm hover:shadow transition-all ${
          isDisabled ? 'opacity-70' : 'cursor-grab active:cursor-grabbing'
        } ${
          isDragging ? 'scale-90 shadow-lg z-50' : ''
        }`}
        draggable={!isDisabled}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <CardContent className="p-3">
          <div className="flex justify-between items-start mb-3">
            <h4 className="font-medium text-sm">{card.title}</h4>
            <div className="flex space-x-1">
              <Badge
                variant="secondary"
                className={getPriorityColor(card.priority)}
              >
                {card.priority}
              </Badge>
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={handleButtonClick}>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    disabled={isDisabled}
                  >
                    <MoreHorizontal className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleEditMFO}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit MFO
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleTransferMFO}>
                    <Share2 className="h-4 w-4 mr-2" />
                    Transfer MFO
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-destructive focus:text-destructive" 
                    onClick={handleDeleteMFO}
                  >
                    <Trash className="h-4 w-4 mr-2" />
                    Delete MFO
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
            {card.description}
          </p>

          <div className="space-y-3">
            <div className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">Progress</span>
                <span>{card.progress}%</span>
              </div>
              <Progress value={card.progress} className={getProgressColor(card.progress)} />
            </div>

            <div className="flex items-center justify-between text-xs mt-2">
              <div className="flex items-center text-muted-foreground">
                <Calendar className="h-3 w-3 mr-1" />
                <span>{card.dueDate}</span>
              </div>
              <div className="flex -space-x-1.5">
                {card.assignees.slice(0, 3).map((assignee, idx) => (
                  <Avatar
                    key={idx}
                    className="h-5 w-5 border-2 border-background"
                  >
                    <AvatarImage src={assignee.avatar} alt={assignee.name} />
                    <AvatarFallback className="text-[8px] bg-primary text-primary-foreground">
                      {assignee.name
                        .split(" ")
                        .map((n) => n[0])
                        .join("")}
                    </AvatarFallback>
                  </Avatar>
                ))}
                {card.assignees.length > 3 && (
                  <div className="h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[8px] border-2 border-background">
                    +{card.assignees.length - 3}
                  </div>
                )}
              </div>
            </div>

           
          </div>
        </CardContent>
      </Card>

      <MFODetailDialog 
        mfo={mfoItem} 
        isOpen={isMFODialogOpen || isEditModalOpen} 
        onOpenChange={(open) => {
          if (isEditModalOpen) {
            setIsEditModalOpen(open);
          } else {
            setIsMFODialogOpen(open);
          }
        }} 
      />

      <TransferMFOModal 
        isOpen={isTransferModalOpen}
        onOpenChange={setIsTransferModalOpen}
        mfo={mfoItem}
        currentColumnId={columnId}
        onTransfer={(newColumnId) => {
          if (onTransfer) {
            onTransfer(card.id, newColumnId);
          }
          setIsTransferModalOpen(false);
        }}
      />

      <DeleteMFOConfirmModal 
        isOpen={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        mfoTitle={card.title}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default TaskCard;
